import type { FC, ReactNode } from 'react';
import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';

export interface CounterButtonProps extends Omit<AntButtonProps, 'className' | 'variant'> {
  children: ReactNode;
  variant?: 'settings' | 'stop' | 'start' | 'primary' | 'default';
  fullWidth?: boolean;
}

/**
 * CounterButton - Counter Page Button Component
 * Pixel-perfect button matching camera-detail-page.png design
 */
export const CounterButton: FC<CounterButtonProps> = ({
  children,
  variant = 'default',
  fullWidth = false,
  ...antdProps
}) => {
  const baseClasses = 'font-semibold transition-all duration-200';

  const variantClasses = {
    settings: 'bg-[#5A5A5A] text-white border-0',
    stop: 'bg-[#E53935] text-white border-0',
    start: 'bg-[#34C759] text-white border-0',
    primary: 'bg-fs-button-primary text-white border-0',
    default: 'bg-white text-fs-text-primary border border-fs-border-subtle',
  };

  const variantStyles = {
    settings: {
      backgroundColor: '#5A5A5A',
      borderColor: 'transparent',
    },
    stop: {
      backgroundColor: '#E53935',
      borderColor: 'transparent',
    },
    start: {
      backgroundColor: '#34C759',
      borderColor: 'transparent',
    },
    primary: {
      backgroundColor: 'var(--fs-button-primary, #007AFF)',
      borderColor: 'transparent',
    },
    default: {
      backgroundColor: '#FFFFFF',
      borderColor: 'var(--fs-border-subtle, #E5E5EA)',
    },
  };

  const widthClasses = fullWidth ? 'w-full' : '';

  const className = [
    baseClasses,
    variantClasses[variant],
    widthClasses,
  ]
    .filter(Boolean)
    .join(' ');

  const textColor = variant === 'default' ? 'var(--fs-text-primary, #1C1C1E)' : '#FFFFFF';

  return (
    <AntButton
      {...antdProps}
      className={`${className} counter-button-no-hover`}
      style={{
        height: 'auto',
        padding: '12px 24px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 'bold',
        '--counter-button-bg': variantStyles[variant].backgroundColor,
        '--counter-button-border': variantStyles[variant].borderColor,
        '--counter-button-color': textColor,
        ...variantStyles[variant],
        color: textColor,
        ...antdProps.style, // Allow style override
      } as React.CSSProperties & {
        '--counter-button-bg': string;
        '--counter-button-border': string;
        '--counter-button-color': string;
      }}
      onMouseEnter={(e) => {
        // Force prevent hover effect
        const target = e.currentTarget;
        const bgColor = variantStyles[variant].backgroundColor;
        const borderColor = variantStyles[variant].borderColor;
        target.style.setProperty('background-color', bgColor, 'important');
        target.style.setProperty('border-color', borderColor, 'important');
        target.style.setProperty('color', textColor, 'important');
      }}
      onMouseLeave={(e) => {
        // Keep the same style on mouse leave
        const target = e.currentTarget;
        const bgColor = variantStyles[variant].backgroundColor;
        const borderColor = variantStyles[variant].borderColor;
        target.style.setProperty('background-color', bgColor, 'important');
        target.style.setProperty('border-color', borderColor, 'important');
        target.style.setProperty('color', textColor, 'important');
      }}
    >
      {children}
    </AntButton>
  );
};

